import React, {
  forwardRef, useEffect, useImperativeHandle, useState,
} from 'react';
import { X } from 'lucide-react';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CrmMailRecipientInput = forwardRef(function CrmMailRecipientInput({
  label,
  value = [],
  onChange,
  onSearch,
  suggestions = [],
}, ref) {
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (input.length >= 1) {
      onSearch?.(input);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  }, [input, onSearch]);

  const commitPending = () => {
    const email = input.trim().replace(/,$/, '');
    if (!email || !EMAIL_RE.test(email) || value.some((r) => r.email === email)) {
      return value;
    }
    const next = [...value, { email }];
    onChange(next);
    setInput('');
    setShowSuggestions(false);
    return next;
  };

  useImperativeHandle(ref, () => ({
    commitPending,
  }));

  const addRecipient = (recipient) => {
    const email = typeof recipient === 'string' ? recipient : recipient.email;
    if (!email || value.some((r) => r.email === email)) return;
    onChange([...value, typeof recipient === 'string' ? { email } : recipient]);
    setInput('');
    setShowSuggestions(false);
  };

  const removeRecipient = (email) => {
    onChange(value.filter((r) => r.email !== email));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitPending();
    }
    if (e.key === 'Backspace' && input === '' && value.length) {
      removeRecipient(value[value.length - 1].email);
    }
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap items-center gap-1 border border-border rounded-lg px-2 py-1.5 min-h-[38px] bg-background">
        <span className="text-xs text-muted-foreground w-8 shrink-0">{label}</span>
        {value.map((r) => (
          <Badge key={r.email} variant="secondary" className="gap-1 pr-1">
            {r.name ? `${r.name} <${r.email}>` : r.email}
            <button type="button" onClick={() => removeRecipient(r.email)} className="hover:text-destructive">
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            commitPending();
            setTimeout(() => setShowSuggestions(false), 150);
          }}
          onFocus={() => input && setShowSuggestions(true)}
          className="border-0 shadow-none focus-visible:ring-0 h-7 flex-1 min-w-[120px] p-0"
          placeholder={value.length ? '' : 'Add recipient…'}
        />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((s) => (
            <li key={s.email}>
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                onMouseDown={(e) => { e.preventDefault(); addRecipient(s); }}
              >
                <span className="font-medium">{s.name}</span>
                {s.designation && <span className="text-muted-foreground"> · {s.designation}</span>}
                <br />
                <span className="text-xs text-muted-foreground">{s.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

export default CrmMailRecipientInput;
